import { z } from "zod";

import { billingRecordSortSchema } from "../../../service/billing-record-sort.models";
import { stripeEventSortSchema } from "../../../service/stripe-event-sort.models";

import {
  BILLABLE_ACTIONS,
  LIMIT_FIELDS,
  MAX_BILLING_INVOICE_PAYMENT_ATTEMPT_NANOCREDITS,
  SUBSCRIPTION_TIERS,
  UNLIMITED_LIMIT_OVERRIDE_VALUE,
} from "./constants";
import { POSTPAID_COLLECTION_MODES } from "./subscriptions";
import { stripeEventStatusSchema } from "./stripe-events";
import { AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS } from "../stripe-types";
import { billingTierLimitsSchema } from "./transport-limits";
import {
  PROMO_CAMPAIGN_CODE_MAX_LENGTH,
  PROMO_CAMPAIGN_MAX_CODE_COUNT,
  PROMO_CAMPAIGN_MAX_REDEMPTIONS_PER_CODE,
  PROMO_CAMPAIGN_NAME_MAX_LENGTH,
  PROMO_CAMPAIGN_PREFIX_MAX_LENGTH,
} from "./promo-campaigns";

export const subscriptionTierSchema = z.enum([
  SUBSCRIPTION_TIERS.FREE,
  SUBSCRIPTION_TIERS.GROWTH,
  SUBSCRIPTION_TIERS.ENTERPRISE,
]);

export const stripeStandardSubscriptionTierSchema = z.enum([
  SUBSCRIPTION_TIERS.GROWTH,
]);

/**
 * Tiers that can be started via Stripe Checkout (the $0 free plan and the
 * paid Growth plan). Enterprise is sales-led and is not a Checkout target.
 */
export const stripeCatalogSubscriptionTierSchema = z.enum([
  SUBSCRIPTION_TIERS.FREE,
  SUBSCRIPTION_TIERS.GROWTH,
]);

/**
 * Purchasable subscription tiers for the paid checkout / portal upgrade flows.
 * Distinct from `StripeCatalogSubscriptionTier`, which also includes the $0
 * `free` tier that is only entered via the dedicated free-plan checkout.
 */
export type StripeStandardSubscriptionTier = z.infer<
  typeof stripeStandardSubscriptionTierSchema
>;

export const stripeDiscountCodeSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") {
      return value;
    }

    const trimmed = value.trim();
    return trimmed.length === 0 ? undefined : trimmed;
  },
  z
    .string()
    .min(1)
    .max(500)
    .refine(hasNoWhitespaceOrControlCharacters, {
      message: "Use a valid Stripe discount code.",
    })
    .optional(),
);

function hasNoWhitespaceOrControlCharacters(value: string): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code <= 32 || code === 127 || character.trim().length === 0) {
      return false;
    }
  }

  return true;
}

export const billableActionSchema = z.enum([
  BILLABLE_ACTIONS.TRAINING_JOB,
  BILLABLE_ACTIONS.EVAL_RUN_SAMPLE,
  BILLABLE_ACTIONS.DEPLOYMENT_ACTIVE,
  BILLABLE_ACTIONS.DEPLOYMENT_GPU_HOUR,
  BILLABLE_ACTIONS.SERVERLESS_INFERENCE,
  BILLABLE_ACTIONS.THIRD_PARTY_SERVERLESS_INFERENCE,
  BILLABLE_ACTIONS.PROXIED_INFERENCE_REQUESTS,
  BILLABLE_ACTIONS.PROXIED_INFERENCE_DATA_BYTES,
  BILLABLE_ACTIONS.OTEL_SPAN_COUNT,
  BILLABLE_ACTIONS.OTEL_SPAN_DATA_BYTES,
  BILLABLE_ACTIONS.HALO_RUN,
  BILLABLE_ACTIONS.HALO_INFERENCE_COST,
  BILLABLE_ACTIONS.SIGNAL_SPAN_CLASSIFICATION,
  BILLABLE_ACTIONS.SIGNAL_TRACE_CLASSIFICATION,
  BILLABLE_ACTIONS.SIGNAL_SESSION_CLASSIFICATION,
]);

export const limitFieldSchema = z.enum([
  LIMIT_FIELDS.PER_MINUTE,
  LIMIT_FIELDS.PER_HOUR,
  LIMIT_FIELDS.PER_DAY,
  LIMIT_FIELDS.PER_MONTH,
  LIMIT_FIELDS.MAX_CONCURRENT,
]);

const optionalAuditReasonSchema = z.string().optional();

function supportsLimitField({
  action,
  field,
}: {
  action: string;
  field: string;
}): boolean {
  if (field === LIMIT_FIELDS.PER_MINUTE) {
    return action === BILLABLE_ACTIONS.SERVERLESS_INFERENCE;
  }

  return true;
}

function addLimitFieldIssue({
  ctx,
  action,
  field,
}: {
  ctx: z.RefinementCtx;
  action: string;
  field: string;
}): void {
  if (supportsLimitField({ action, field })) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["field"],
    message: `${field} is not supported for ${action}.`,
  });
}

export const superAdminBillingTeamDetailParams = z.object({
  teamId: z.string().min(1),
});

export type SuperAdminBillingTeamDetailParams = z.infer<
  typeof superAdminBillingTeamDetailParams
>;

export const superAdminBillingListCreditTransactionsParams = z.object({
  teamId: z.string().min(1),
  direction: z.enum(["all", "credits", "debits"]).default("all"),
  limit: z.number().int().min(1).max(100).default(50),
});

export type SuperAdminBillingListCreditTransactionsParams = z.infer<
  typeof superAdminBillingListCreditTransactionsParams
>;

export const superAdminBillingCreditTransactionReceiptParams = z.object({
  teamId: z.string().min(1),
  creditTransactionId: z.string().min(1),
});

export type SuperAdminBillingCreditTransactionReceiptParams = z.infer<
  typeof superAdminBillingCreditTransactionReceiptParams
>;

export const superAdminBillingListBillingRecordsParams = z.object({
  teamId: z.string().min(1).optional(),
  action: billableActionSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
  createdAfter: z.date().optional(),
  createdBefore: z.date().optional(),
  sort: billingRecordSortSchema.nullish(),
});

export type SuperAdminBillingListBillingRecordsParams = z.infer<
  typeof superAdminBillingListBillingRecordsParams
>;

// 'billing:%' keys belong to old charge debits. Credit external_id lookups
// and the noncharge unique index leave them out, so an admin key with that
// prefix would be neither found nor idempotent.
const NON_CHARGE_EXTERNAL_ID = /^(?!billing:)/;
const NON_CHARGE_EXTERNAL_ID_MESSAGE =
  "externalId must not start with 'billing:'";

export const superAdminBillingGrantCreditsParams = z.object({
  teamId: z.string().min(1),
  amountNanocredits: z.string().regex(/^[1-9]\d*$/),
  reason: optionalAuditReasonSchema,
  referenceId: z.string().min(1).optional(),
  externalId: z
    .string()
    .min(1)
    .regex(NON_CHARGE_EXTERNAL_ID, NON_CHARGE_EXTERNAL_ID_MESSAGE)
    .optional(),
});

export type SuperAdminBillingGrantCreditsParams = z.infer<
  typeof superAdminBillingGrantCreditsParams
>;

export const superAdminBillingManualCreditAdjustmentParams = z.object({
  teamId: z.string().min(1),
  amountNanocredits: z.string().regex(/^-?[1-9]\d*$/),
  reason: optionalAuditReasonSchema,
  referenceId: z.string().min(1).optional(),
  /** Idempotency key: a positive adjustment repeated with the same key is applied once. */
  externalId: z
    .string()
    .min(1)
    .max(255)
    .regex(NON_CHARGE_EXTERNAL_ID, NON_CHARGE_EXTERNAL_ID_MESSAGE)
    .optional(),
});

export type SuperAdminBillingManualCreditAdjustmentParams = z.infer<
  typeof superAdminBillingManualCreditAdjustmentParams
>;

export const superAdminBillingSetManualTierOverrideParams = z.object({
  teamId: z.string().min(1),
  tier: subscriptionTierSchema,
  reason: optionalAuditReasonSchema,
});

export type SuperAdminBillingSetManualTierOverrideParams = z.infer<
  typeof superAdminBillingSetManualTierOverrideParams
>;

export const superAdminBillingClearManualTierOverrideParams = z.object({
  teamId: z.string().min(1),
});

export type SuperAdminBillingClearManualTierOverrideParams = z.infer<
  typeof superAdminBillingClearManualTierOverrideParams
>;

export const superAdminBillingSetLimitOverrideParams = z
  .object({
    teamId: z.string().min(1),
    action: billableActionSchema,
    field: limitFieldSchema,
    value: z.number().int().min(UNLIMITED_LIMIT_OVERRIDE_VALUE),
    reason: optionalAuditReasonSchema,
  })
  .superRefine(({ action, field, value }, ctx) => {
    addLimitFieldIssue({ ctx, action, field });
    // Concurrency enforcement requires a bounded value.
    if (
      value === UNLIMITED_LIMIT_OVERRIDE_VALUE &&
      field === LIMIT_FIELDS.MAX_CONCURRENT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `${field} cannot be unlimited.`,
      });
    }
  });

export type SuperAdminBillingSetLimitOverrideParams = z.infer<
  typeof superAdminBillingSetLimitOverrideParams
>;

export const superAdminBillingRemoveLimitOverrideParams = z
  .object({
    teamId: z.string().min(1),
    action: billableActionSchema,
    field: limitFieldSchema,
  })
  .superRefine(({ action, field }, ctx) => {
    addLimitFieldIssue({ ctx, action, field });
  });

export type SuperAdminBillingRemoveLimitOverrideParams = z.infer<
  typeof superAdminBillingRemoveLimitOverrideParams
>;

export const superAdminBillingSetPlatformGuardrailParams = z
  .object({
    teamId: z.string().min(1).optional(),
    action: billableActionSchema,
    field: limitFieldSchema,
    value: z.number().int().nonnegative(),
    reason: optionalAuditReasonSchema,
  })
  .superRefine(({ action, field }, ctx) => {
    addLimitFieldIssue({ ctx, action, field });
  });

export type SuperAdminBillingSetPlatformGuardrailParams = z.infer<
  typeof superAdminBillingSetPlatformGuardrailParams
>;

export const superAdminBillingClearPlatformGuardrailParams = z
  .object({
    teamId: z.string().min(1).optional(),
    action: billableActionSchema,
    field: limitFieldSchema,
  })
  .superRefine(({ action, field }, ctx) => {
    addLimitFieldIssue({ ctx, action, field });
  });

export type SuperAdminBillingClearPlatformGuardrailParams = z.infer<
  typeof superAdminBillingClearPlatformGuardrailParams
>;

export const superAdminBillingCreateCustomPlanParams = z.object({
  teamId: z.string().min(1),
  version: z.number().int().positive(),
  name: z.string().min(1),
  limits: billingTierLimitsSchema,
  externalContractRef: z.string().min(1).optional(),
  notes: z.string().min(1).optional(),
});

export type SuperAdminBillingCreateCustomPlanParams = z.infer<
  typeof superAdminBillingCreateCustomPlanParams
>;

export const superAdminBillingActivateCustomPlanParams = z.object({
  teamId: z.string().min(1),
  planId: z.string().min(1),
});

export type SuperAdminBillingActivateCustomPlanParams = z.infer<
  typeof superAdminBillingActivateCustomPlanParams
>;

export const superAdminBillingCancelCustomPlanParams = z.object({
  teamId: z.string().min(1),
  planId: z.string().min(1),
});

export type SuperAdminBillingCancelCustomPlanParams = z.infer<
  typeof superAdminBillingCancelCustomPlanParams
>;

export const superAdminBillingReviseCustomPlanParams = z.object({
  teamId: z.string().min(1),
  sourcePlanId: z.string().min(1),
  name: z.string().min(1).optional(),
  limits: billingTierLimitsSchema.optional(),
  externalContractRef: z.string().min(1).optional(),
  notes: z.string().min(1).optional(),
});

export type SuperAdminBillingReviseCustomPlanParams = z.infer<
  typeof superAdminBillingReviseCustomPlanParams
>;

export const superAdminBillingListPlatformGuardrailsParams = z.object({
  teamId: z.string().min(1).optional(),
  includeGlobal: z.boolean().default(true),
});

export type SuperAdminBillingListPlatformGuardrailsParams = z.infer<
  typeof superAdminBillingListPlatformGuardrailsParams
>;

const superAdminBillingStripeCustomerContextParams = z.object({
  customerEmail: z.string().email().optional(),
  teamName: z.string().min(1).optional(),
  teamSlug: z.string().min(1).optional(),
});

const superAdminBillingStripeUrlParams = z.object({
  successUrl: z.string().url().max(2048),
  cancelUrl: z.string().url().max(2048),
});

export const superAdminBillingCreateSubscriptionCheckoutSessionParams = z
  .object({
    teamId: z.string().min(1),
    targetTier: stripeStandardSubscriptionTierSchema,
  })
  .merge(superAdminBillingStripeUrlParams)
  .merge(superAdminBillingStripeCustomerContextParams);

export type SuperAdminBillingCreateSubscriptionCheckoutSessionParams = z.infer<
  typeof superAdminBillingCreateSubscriptionCheckoutSessionParams
>;

export const superAdminBillingCreateCreditCheckoutSessionParams = z
  .object({
    teamId: z.string().min(1),
    amountCents: z.number().int().min(100).max(1_000_000),
    discountCode: stripeDiscountCodeSchema,
  })
  .merge(superAdminBillingStripeUrlParams)
  .merge(superAdminBillingStripeCustomerContextParams);

export type SuperAdminBillingCreateCreditCheckoutSessionParams = z.infer<
  typeof superAdminBillingCreateCreditCheckoutSessionParams
>;

export const superAdminBillingCreatePortalSessionParams = z
  .object({
    teamId: z.string().min(1),
    returnUrl: z.string().url().max(2048),
  })
  .merge(superAdminBillingStripeCustomerContextParams);

export type SuperAdminBillingCreatePortalSessionParams = z.infer<
  typeof superAdminBillingCreatePortalSessionParams
>;

export const superAdminBillingCreateSubscriptionPortalSessionParams = z
  .object({
    teamId: z.string().min(1),
    returnUrl: z.string().url().max(2048),
    flow: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("cancel") }),
      z.object({
        kind: z.literal("update"),
        targetTier: stripeStandardSubscriptionTierSchema,
      }),
    ]),
  })
  .merge(superAdminBillingStripeCustomerContextParams);

export type SuperAdminBillingCreateSubscriptionPortalSessionParams = z.infer<
  typeof superAdminBillingCreateSubscriptionPortalSessionParams
>;

/**
 * Admin unstick (INF-4110): cancel the team's Stripe subscription and
 * reconcile so a checkout-created-but-unusable subscription row stops
 * blocking re-checkout.
 */
export const superAdminBillingClearStripeSubscriptionParams = z.object({
  teamId: z.string().min(1),
});

export type SuperAdminBillingClearStripeSubscriptionParams = z.infer<
  typeof superAdminBillingClearStripeSubscriptionParams
>;

export const superAdminBillingUpdateAutoTopUpSettingsParams = z.object({
  teamId: z.string().min(1),
  enabled: z.boolean(),
  amountCents: z.number().int().min(1_000).max(1_000_000).nullable(),
  monthlyLimitCents: z
    .number()
    .int()
    .min(1_000)
    .max(AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS)
    .nullable()
    .optional(),
  consentVersion: z.string().min(1).max(100).nullable().optional(),
  thresholdNanocredits: z
    .string()
    .regex(/^-?\d+$/)
    .nullable(),
});

export type SuperAdminBillingUpdateAutoTopUpSettingsParams = z.infer<
  typeof superAdminBillingUpdateAutoTopUpSettingsParams
>;

export const superAdminBillingExecuteAutoTopUpParams = z.object({
  teamId: z.string().min(1),
});

export type SuperAdminBillingExecuteAutoTopUpParams = z.infer<
  typeof superAdminBillingExecuteAutoTopUpParams
>;

export const superAdminBillingFinalizePostpaidInvoiceParams = z.object({
  teamId: z.string().min(1),
  /**
   * Invoice cutoff: claims uninvoiced usage whose service period starts before
   * this instant (e.g. `2026-10-01T00:00:00Z` closes September UTC). Defaults
   * to now.
   */
  finalizedAt: z.string().datetime().optional(),
});

export type SuperAdminBillingFinalizePostpaidInvoiceParams = z.infer<
  typeof superAdminBillingFinalizePostpaidInvoiceParams
>;

export const superAdminBillingInvoiceActionParams = z.object({
  teamId: z.string().min(1),
  invoiceId: z.string().min(1),
});

export type SuperAdminBillingInvoiceActionParams = z.infer<
  typeof superAdminBillingInvoiceActionParams
>;

export const superAdminBillingSetTeamBillingStatusParams = z.object({
  teamId: z.string().min(1),
  status: z.enum(["current", "grace", "suspended"]),
  currentInvoiceId: z.string().min(1).optional(),
  suspensionReason: z.string().trim().min(1).max(200).optional(),
});

export type SuperAdminBillingSetTeamBillingStatusParams = z.infer<
  typeof superAdminBillingSetTeamBillingStatusParams
>;

export const superAdminBillingSetPostpaidBillingParams = z.object({
  teamId: z.string().min(1),
  enabled: z.boolean(),
  collectionMode: z.nativeEnum(POSTPAID_COLLECTION_MODES).optional(),
  reason: z.string().trim().max(500).optional(),
  exposureLimitNanocredits: z
    .string()
    .regex(/^[1-9]\d*$/)
    // Reject values above the Postgres bigint max so a fat-fingered cap
    // surfaces as input validation instead of an insert error. Guarded
    // because zod still runs refinements when the regex check fails, and
    // BigInt() throws on non-numeric input.
    .refine(
      (value) =>
        !/^\d+$/.test(value) || BigInt(value) <= 9_223_372_036_854_775_807n,
      { message: "Exposure limit exceeds the maximum storable value" },
    )
    .optional(),
  overrideUncollectable: z.boolean().optional(),
});

export type SuperAdminBillingSetPostpaidBillingParams = z.infer<
  typeof superAdminBillingSetPostpaidBillingParams
>;

export const superAdminBillingMarkInvoicePaidOffPlatformParams = z.object({
  teamId: z.string().min(1),
  invoiceId: z.string().min(1),
  paymentReference: z.string().trim().min(1).max(255),
  amountNanocredits: z
    .string()
    .regex(/^[1-9]\d*$/)
    .refine(
      (amountNanocredits) => {
        try {
          return (
            BigInt(amountNanocredits) <=
            MAX_BILLING_INVOICE_PAYMENT_ATTEMPT_NANOCREDITS
          );
        } catch {
          return false;
        }
      },
      { message: "Payment amount exceeds the supported maximum." },
    ),
  expectedOutstandingNanocredits: z.string().regex(/^[1-9]\d*$/),
  paidAt: z.coerce.date().optional(),
});

export type SuperAdminBillingMarkInvoicePaidOffPlatformParams = z.infer<
  typeof superAdminBillingMarkInvoicePaidOffPlatformParams
>;

export const superAdminBillingPreviewInvoiceSummaryParams = z.object({
  teamId: z.string().min(1),
  /** Inclusive UTC service-period start, e.g. `2026-09-01T00:00:00Z`. */
  periodStart: z.string().datetime(),
  /** Exclusive UTC service-period end, e.g. `2026-10-01T00:00:00Z`. */
  periodEnd: z.string().datetime(),
  /** Sealed bound returned by an earlier preview call; resumes that preview. */
  recordsInsertedBefore: z.string().datetime().optional(),
});

export type SuperAdminBillingPreviewInvoiceSummaryParams = z.infer<
  typeof superAdminBillingPreviewInvoiceSummaryParams
>;

export const superAdminBillingApplyInvoiceSummaryParams = z.object({
  teamId: z.string().min(1),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  recordsInsertedBefore: z.string().datetime(),
  expectedSubtotalNanocredits: z.string().regex(/^[1-9]\d*$/),
  expectedSelectionDigest: z.string().regex(/^[0-9a-f]{64}$/),
  dueAt: z.string().datetime().optional(),
});

export type SuperAdminBillingApplyInvoiceSummaryParams = z.infer<
  typeof superAdminBillingApplyInvoiceSummaryParams
>;

export const superAdminBillingSetInvoiceReasoningTokensSupplementParams =
  z.object({
    teamId: z.string().min(1),
    invoiceId: z.string().uuid(),
    /** Where the counts come from; the invoice export names it. */
    source: z.string().trim().min(1).max(500),
    /** Inclusive UTC created_at start of the counted records. */
    windowStart: z.string().datetime(),
    /** Exclusive UTC created_at end of the counted records. */
    windowEnd: z.string().datetime(),
    /** Reasoning tokens of the records in the window that carry none. */
    lines: z
      .array(
        z.object({
          serviceType: z.string().min(1),
          model: z.string().min(1),
          reasoningTokens: z.string().regex(/^\d+$/),
        }),
      )
      .min(1)
      .max(500),
  });

export type SuperAdminBillingSetInvoiceReasoningTokensSupplementParams =
  z.infer<typeof superAdminBillingSetInvoiceReasoningTokensSupplementParams>;

export const superAdminBillingBackfillInvoiceSummaryUsageParams = z.object({
  teamId: z.string().min(1),
  invoiceId: z.string().uuid(),
});

export type SuperAdminBillingBackfillInvoiceSummaryUsageParams = z.infer<
  typeof superAdminBillingBackfillInvoiceSummaryUsageParams
>;

export const invoiceDetailExportFormatSchema = z
  .enum(["xlsx", "csv"])
  .default("xlsx");

export const superAdminBillingExportInvoiceDetailParams = z.object({
  teamId: z.string().min(1),
  invoiceId: z.string().uuid(),
  format: invoiceDetailExportFormatSchema,
  /** Names the customer in the file name and sheet titles instead of the billing profile's legal name or the team name. */
  customerName: z.string().trim().min(1).max(200).optional(),
});

export type SuperAdminBillingExportInvoiceDetailParams = z.infer<
  typeof superAdminBillingExportInvoiceDetailParams
>;

export const superAdminBillingGetPostpaidEnablementReadinessParams = z.object({
  teamId: z.string().min(1),
});

export type SuperAdminBillingGetPostpaidEnablementReadinessParams = z.infer<
  typeof superAdminBillingGetPostpaidEnablementReadinessParams
>;

export const superAdminBillingListStripeEventsParams = z.object({
  limit: z.number().int().min(1).max(100).default(50),
  search: z.string().trim().max(200).optional(),
  status: stripeEventStatusSchema.optional(),
  sort: stripeEventSortSchema.nullish(),
});

export type SuperAdminBillingListStripeEventsParams = z.infer<
  typeof superAdminBillingListStripeEventsParams
>;

export const superAdminBillingSyncStripeEventsParams = z.object({
  limit: z.number().int().min(1).max(100).default(50),
  createdAfter: z.number().int().min(0).optional(),
});

export type SuperAdminBillingSyncStripeEventsParams = z.infer<
  typeof superAdminBillingSyncStripeEventsParams
>;

const optionalPromoCodeText = (maxLength: number) =>
  z.preprocess(
    (value) =>
      typeof value === "string" && value.trim().length === 0
        ? undefined
        : value,
    z
      .string()
      .trim()
      .min(1)
      .max(maxLength)
      .regex(/^[A-Za-z0-9]+(?:[-_][A-Za-z0-9]+)*$/, {
        message: "Use only letters, numbers, hyphens, and underscores.",
      })
      .transform((value) => value.toUpperCase())
      .optional(),
  );

export const superAdminCreatePromoCampaignParams = z
  .object({
    name: z.string().trim().min(1).max(PROMO_CAMPAIGN_NAME_MAX_LENGTH),
    codePrefix: optionalPromoCodeText(PROMO_CAMPAIGN_PREFIX_MAX_LENGTH),
    exactCode: optionalPromoCodeText(PROMO_CAMPAIGN_CODE_MAX_LENGTH),
    discountKind: z.enum(["percent", "amount"]),
    percentOff: z.number().int().min(1).max(100).optional(),
    amountOffCents: z.number().int().min(1).optional(),
    codeCount: z.number().int().min(1).max(PROMO_CAMPAIGN_MAX_CODE_COUNT),
    redemptionsPerCode: z
      .number()
      .int()
      .min(1)
      .max(PROMO_CAMPAIGN_MAX_REDEMPTIONS_PER_CODE),
    maxRedemptions: z.number().int().min(1).optional(),
    expiresAt: z.coerce.date().optional(),
    minAmountCents: z.number().int().min(1).optional(),
    firstTimeCustomer: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.exactCode && value.codeCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "An exact code can only be used when codeCount is 1.",
        path: ["exactCode"],
      });
    }
    if (value.exactCode && value.codePrefix) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Use either an exact code or a generated-code prefix, not both.",
        path: ["codePrefix"],
      });
    }
    if (
      value.maxRedemptions != null &&
      value.maxRedemptions > value.codeCount * value.redemptionsPerCode
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "The campaign cap cannot exceed codeCount × redemptionsPerCode.",
        path: ["maxRedemptions"],
      });
    }
    if (value.discountKind === "percent") {
      if (value.percentOff == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Percent-off campaigns require percentOff.",
          path: ["percentOff"],
        });
      }
      if (value.amountOffCents != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Percent-off campaigns cannot also set amountOffCents.",
          path: ["amountOffCents"],
        });
      }
      return;
    }
    if (value.amountOffCents == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount-off campaigns require amountOffCents.",
        path: ["amountOffCents"],
      });
    }
    if (value.percentOff != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount-off campaigns cannot also set percentOff.",
        path: ["percentOff"],
      });
    }
  });

export type SuperAdminCreatePromoCampaignParams = z.infer<
  typeof superAdminCreatePromoCampaignParams
>;

export const superAdminListPromoCampaignsParams = z.object({
  cursor: z.string().trim().min(1).optional(),
  limit: z.number().int().min(1).max(100).default(25),
});

export type SuperAdminListPromoCampaignsParams = z.infer<
  typeof superAdminListPromoCampaignsParams
>;

export const superAdminGetPromoCampaignParams = z.object({
  campaignId: z.string().uuid(),
});

export type SuperAdminGetPromoCampaignParams = z.infer<
  typeof superAdminGetPromoCampaignParams
>;

export const superAdminListPromoCodesParams = z.object({
  campaignId: z.string().uuid(),
  cursor: z.string().trim().min(1).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export type SuperAdminListPromoCodesParams = z.infer<
  typeof superAdminListPromoCodesParams
>;

export const superAdminListPromoRedemptionsParams = z.object({
  campaignId: z.string().uuid(),
  cursor: z.string().trim().min(1).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export type SuperAdminListPromoRedemptionsParams = z.infer<
  typeof superAdminListPromoRedemptionsParams
>;

/**
 * Public /promo lookup input. Unauthenticated, so the code is constrained to
 * the same shape the minter produces rather than accepting arbitrary text.
 */
export const describePromoCodeParams = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(PROMO_CAMPAIGN_CODE_MAX_LENGTH)
    .regex(/^[A-Za-z0-9]+(?:[-_][A-Za-z0-9]+)*$/, {
      message: "Use only letters, numbers, hyphens, and underscores.",
    }),
});

export type DescribePromoCodeParams = z.infer<typeof describePromoCodeParams>;

export const superAdminDisablePromoCampaignParams = z.object({
  campaignId: z.string().uuid(),
});

export type SuperAdminDisablePromoCampaignParams = z.infer<
  typeof superAdminDisablePromoCampaignParams
>;
